import { type FC, type RefObject, useEffect, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { Box, Button, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import {
  Input,
  RichTextEditor,
  RichTextEditorHandle,
  Textarea,
} from '@factorialco/f0-react/dist/experimental'
import { SUPPORTED_LANGUAGES_LABELS } from 'config/locales'
import {
  CreateItManagementDocumentTemplateDocument,
  ItManagementDocumentTemplatesListDocument,
  UpdateItManagementDocumentTemplateDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

interface TemplateFormModalProps {
  template?: {
    id: string
    title: string
    templateType: string
    language: string
    twoLanguages: boolean
    languageSecond?: string | null
    policyText?: string | null
    policyTextSecondLanguage?: string | null
  }
  onClose: () => void
}

const LANGUAGE_OPTIONS = Object.entries(SUPPORTED_LANGUAGES_LABELS)
  .filter(([code]) => !['en', 'eo'].includes(code))
  .map(([value, label]) => ({ value, label }))

export const TemplateFormModal: FC<TemplateFormModalProps> = ({ template, onClose }) => {
  const isEditing = !!template

  const policyTextEditorRef = useRef<RichTextEditorHandle>(null)
  const policyTextSecondLanguageEditorRef = useRef<RichTextEditorHandle>(null)
  const policyTextWrapperRef = useRef<HTMLDivElement>(null)
  const policyTextSecondLanguageWrapperRef = useRef<HTMLDivElement>(null)

  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()

  const [formData, setFormData] = useState({
    title: template?.title || '',
    templateType: template?.templateType || 'assign',
    language: template?.language || 'en-us',
    twoLanguages: template?.twoLanguages || false,
    languageSecond: template?.languageSecond || '',
    policyText: template?.policyText || '',
    policyTextSecondLanguage: template?.policyTextSecondLanguage || '',
  })

  const [createTemplate, { loading: creating }] = useMutation(
    CreateItManagementDocumentTemplateDocument,
    {
      refetchQueries: [ItManagementDocumentTemplatesListDocument],
      onCompleted: () => {
        onClose()
      },
    }
  )

  const [updateTemplate, { loading: updating }] = useMutation(
    UpdateItManagementDocumentTemplateDocument,
    {
      refetchQueries: [ItManagementDocumentTemplatesListDocument],
      onCompleted: () => {
        onClose()
      },
    }
  )

  const handleSubmit = () => {
    if (isEditing) {
      updateTemplate({
        variables: {
          id: template.id,
          ...formData,
        },
      })
    } else {
      createTemplate({
        variables: formData,
      })
    }
  }

  const loading = creating || updating
  const isDisabled = !formData.title || (formData.twoLanguages && !formData.languageSecond)

  useEffect(() => {
    if (!canUseRichTextEditor) return

    const cleanupFunctions: Array<(() => void) | undefined> = []

    const cleanPastedHTML = (html: string): string => {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html

      const removeInlineStylesAndAttributes = (element: Element) => {
        if (element instanceof HTMLElement) {
          element.removeAttribute('style')
          element.removeAttribute('class')
          element.removeAttribute('lang')
          element.removeAttribute('dir')
          element.removeAttribute('data-mce-style')
          element.removeAttribute('data-mce-selected')
          element.removeAttribute('face')
          element.removeAttribute('size')
          element.removeAttribute('color')
        }

        Array.from(element.children).forEach((child) => {
          removeInlineStylesAndAttributes(child)
        })
      }

      removeInlineStylesAndAttributes(tempDiv)

      return tempDiv.innerHTML
    }

    const setupPasteHandler = (
      wrapperRef: RefObject<HTMLDivElement>,
      editorRef: RefObject<RichTextEditorHandle>
    ) => {
      const trySetup = (attempts = 0) => {
        if (attempts > 10) return

        const wrapper = wrapperRef.current
        if (!wrapper) {
          setTimeout(() => trySetup(attempts + 1), 100)
          return
        }

        const contentEditableElement = wrapper.querySelector('[contenteditable="true"]')
        if (!contentEditableElement || !(contentEditableElement instanceof HTMLElement)) {
          setTimeout(() => trySetup(attempts + 1), 100)
          return
        }

        const getEditorInsertContent = (): ((content: string) => void) | null => {
          const editor = editorRef.current
          if (!editor) return null

          const editorObj: Record<string, unknown> = Object(editor)
          const editorInstance =
            editorObj.editor ||
            (typeof editorObj.getEditor === 'function' ? editorObj.getEditor() : undefined) ||
            editorObj.tiptapEditor ||
            editorObj._editor

          if (!editorInstance || typeof editorInstance !== 'object') return null

          const editorInstanceObj: Record<string, unknown> = Object(editorInstance)
          const commands = editorInstanceObj.commands
          if (!commands || typeof commands !== 'object') return null

          const commandsObj: Record<string, unknown> = Object(commands)
          const insertContent = commandsObj.insertContent
          if (typeof insertContent !== 'function') return null

          return (content: string) => {
            Function.prototype.call.call(insertContent, null, content)
          }
        }

        const insertContent = getEditorInsertContent()

        const handlePaste = (event: ClipboardEvent) => {
          event.preventDefault()
          event.stopPropagation()

          const clipboardData = event.clipboardData
          if (!clipboardData) return

          const html = clipboardData.getData('text/html')
          const plainText = clipboardData.getData('text/plain')

          if (html) {
            const cleanedHTML = cleanPastedHTML(html)

            if (insertContent) {
              insertContent(cleanedHTML)
            } else {
              const selection = window.getSelection()
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0)
                range.deleteContents()

                const tempDiv = document.createElement('div')
                tempDiv.innerHTML = cleanedHTML

                const fragment = document.createDocumentFragment()
                while (tempDiv.firstChild) {
                  fragment.appendChild(tempDiv.firstChild)
                }

                range.insertNode(fragment)
                range.collapse(false)
                selection.removeAllRanges()
                selection.addRange(range)
              }
            }
          } else if (plainText) {
            if (insertContent) {
              insertContent(plainText)
            } else {
              const selection = window.getSelection()
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0)
                range.deleteContents()
                range.insertNode(document.createTextNode(plainText))
                range.collapse(false)
                selection.removeAllRanges()
                selection.addRange(range)
              }
            }
          }
        }

        contentEditableElement.addEventListener('paste', handlePaste, true)

        cleanupFunctions.push(() => {
          contentEditableElement.removeEventListener('paste', handlePaste, true)
        })
      }

      trySetup()
    }

    setupPasteHandler(policyTextWrapperRef, policyTextEditorRef)
    setupPasteHandler(policyTextSecondLanguageWrapperRef, policyTextSecondLanguageEditorRef)

    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup?.())
    }
  }, [canUseRichTextEditor])

  return (
    <Modal
      data-testid="template-form-modal"
      title={
        isEditing
          ? i18n.t('it_management.settings.document_templates.edit_modal.title')
          : i18n.t('it_management.settings.document_templates.create_modal.title')
      }
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <Button type="secondary" onClick={onClose} disabled={loading}>
            {i18n.t('common.cancel')}
          </Button>

          <Button type="primary" submit disabled={isDisabled}>
            {isEditing
              ? i18n.t('common.save')
              : i18n.t('it_management.settings.document_templates.create_modal.confirm')}
          </Button>
        </Box>
      }
    >
      <Box gap="s16">
        <Input
          label={i18n.t('it_management.settings.document_templates.form.title')}
          value={formData.title}
          onChange={(value: string) => setFormData({ ...formData, title: value })}
          required
        />

        <F0Select
          label={i18n.t('it_management.settings.document_templates.form.language')}
          value={formData.language}
          onChange={(value: string) => setFormData({ ...formData, language: value })}
          options={LANGUAGE_OPTIONS}
          required
        />

        <Box gap="s8">
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('it_management.settings.document_templates.form.template_type')}
          </Text.Normal>
          <Box flexDirection="row" gap="s16">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="templateType"
                value="assign"
                checked={formData.templateType === 'assign'}
                onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              />
              <Text.Normal size="200" color="grey800">
                {i18n.t('it_management.settings.document_templates.types.assign')}
              </Text.Normal>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="radio"
                name="templateType"
                value="unassign"
                checked={formData.templateType === 'unassign'}
                onChange={(e) => setFormData({ ...formData, templateType: e.target.value })}
              />
              <Text.Normal size="200" color="grey800">
                {i18n.t('it_management.settings.document_templates.types.unassign')}
              </Text.Normal>
            </label>
          </Box>
        </Box>

        {richTextEditorLoading ? (
          <RichTextEditor.Skeleton />
        ) : canUseRichTextEditor ? (
          <Box gap="s8">
            <Text.Normal size="200" weight="medium" color="grey800">
              {i18n.t('it_management.settings.document_templates.form.policy_text')}
            </Text.Normal>
            <div
              ref={policyTextWrapperRef}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            >
              <RichTextEditor
                ref={policyTextEditorRef}
                {...editorConfig}
                onChange={(data) => {
                  setFormData({ ...formData, policyText: data?.value || '' })
                }}
                placeholder={i18n.t('it_management.settings.document_templates.form.policy_text')}
                initialEditorState={{ content: formData.policyText || '' }}
                title={i18n.t('it_management.settings.document_templates.form.policy_text')}
              />
            </div>
          </Box>
        ) : (
          <Textarea
            label={i18n.t('it_management.settings.document_templates.form.policy_text')}
            value={formData.policyText}
            onChange={(value: string) => setFormData({ ...formData, policyText: value })}
            rows={3}
          />
        )}

        <Box flexDirection="row" gap="s12" alignItems="center">
          <Checkbox
            checked={formData.twoLanguages}
            onChange={(e) => setFormData({ ...formData, twoLanguages: e.target.checked })}
            size="large"
          />
          <Text.Normal size="200" weight="medium" color="grey800">
            {i18n.t('it_management.settings.document_templates.form.two_languages')}
          </Text.Normal>
        </Box>

        {formData.twoLanguages && (
          <>
            <F0Select
              label={i18n.t('it_management.settings.document_templates.form.language_second')}
              value={formData.languageSecond}
              onChange={(value: string) => setFormData({ ...formData, languageSecond: value })}
              options={LANGUAGE_OPTIONS}
              required
            />

            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <Box gap="s8">
                <Text.Normal size="200" weight="medium" color="grey800">
                  {i18n.t(
                    'it_management.settings.document_templates.form.policy_text_second_language'
                  )}
                </Text.Normal>
                <div
                  ref={policyTextSecondLanguageWrapperRef}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                >
                  <RichTextEditor
                    ref={policyTextSecondLanguageEditorRef}
                    {...editorConfig}
                    onChange={(data) => {
                      setFormData({
                        ...formData,
                        policyTextSecondLanguage: data?.value || '',
                      })
                    }}
                    placeholder={i18n.t(
                      'it_management.settings.document_templates.form.policy_text_second_language'
                    )}
                    initialEditorState={{
                      content: formData.policyTextSecondLanguage || '',
                    }}
                    title={i18n.t(
                      'it_management.settings.document_templates.form.policy_text_second_language'
                    )}
                  />
                </div>
              </Box>
            ) : (
              <Textarea
                label={i18n.t(
                  'it_management.settings.document_templates.form.policy_text_second_language'
                )}
                value={formData.policyTextSecondLanguage}
                onChange={(value: string) =>
                  setFormData({ ...formData, policyTextSecondLanguage: value })
                }
                rows={3}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}
